import React, { useState, useRef, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import VerificationCodeInput from '../../email-verification/components/VerificationCodeInput';
import { supabase } from '../../../lib/supabase';

const COOLDOWN_SECONDS = 60;

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1); // 1 = email, 2 = code
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | verifying | success | error
  const [cooldown, setCooldown] = useState(0);
  const isSendingRef = useRef(false);
  const inputRef = useRef(null);

  // Restore cooldown from localStorage + focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      // Always reset to step 1 on open — prevents stale step=2 UI when user
      // closed mid-flow; they must re-enter email to continue.
      setStep(1);
      setCode('');
      setError('');
      setStatus('idle');

      const savedAt = localStorage.getItem('forgot_pw_cooldown_at');
      if (savedAt) {
        const elapsed = Math.floor((Date.now() - parseInt(savedAt, 10)) / 1000);
        const remaining = Math.max(COOLDOWN_SECONDS - elapsed, 0);
        setCooldown(remaining);
        if (remaining <= 0) localStorage.removeItem('forgot_pw_cooldown_at');
      }
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setStep(1);
      setEmail('');
      setCode('');
      setError('');
      setStatus('idle');
      isSendingRef.current = false;
    }
  }, [isOpen]);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      const savedAt = localStorage.getItem('forgot_pw_cooldown_at');
      if (!savedAt) {
        setCooldown(0);
        clearInterval(timer);
        return;
      }
      const elapsed = Math.floor((Date.now() - parseInt(savedAt, 10)) / 1000);
      const remaining = Math.max(COOLDOWN_SECONDS - elapsed, 0);
      setCooldown(remaining);
      if (remaining <= 0) {
        localStorage.removeItem('forgot_pw_cooldown_at');
        clearInterval(timer);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  // Auto-verify when code reaches 8 characters (debounced to avoid mis-paste trigger)
  useEffect(() => {
    if (code?.length !== 8 || status === 'verifying' || status === 'success') return;
    const t = setTimeout(() => handleVerifyCode(), 250);
    return () => clearTimeout(t);
  }, [code]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const validateEmail = (value) => {
    if (!value.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
    return '';
  };

  // Step 1: Send reset code
  const handleSendCode = async (e) => {
    e?.preventDefault();
    if (isSendingRef.current || cooldown > 0) return;

    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      return;
    }

    isSendingRef.current = true;
    setStatus('sending');
    setError('');
    localStorage.setItem('forgot_pw_cooldown_at', Date.now().toString());
    setCooldown(COOLDOWN_SECONDS);

    try {
      // Edge function generates the code server-side (service-role) and emails it.
      // The code is never exposed to the browser.
      const { error: fnError } = await supabase.functions.invoke('send-reset-code', {
        body: { email: email.trim().toLowerCase() },
      });
      if (fnError) throw fnError;

      setStep(2);
      setStatus('idle');
    } catch (err) {
      if (import.meta.env?.DEV) console.error('send-reset-code:', err);
      setStatus('error');
      // Generic message — do not leak whether email exists or internal error detail
      setError('Unable to process request. Please try again later.');
      localStorage.removeItem('forgot_pw_cooldown_at');
      setCooldown(0);
    } finally {
      isSendingRef.current = false;
    }
  };

  // Step 2: Verify code + generate recovery session
  const handleVerifyCode = async () => {
    if (status === 'verifying' || status === 'success' || code?.length !== 8) return;

    setStatus('verifying');
    setError('');

    try {
      // Verify code + get recovery token via Edge Function
      const { data, error: fnError } = await supabase.functions.invoke('verify-reset-code', {
        body: { email, code },
      });

      if (fnError) throw fnError;

      const result = data;

      if (result?.status === 'success' && result?.token_hash) {
        // Establish recovery session using token_hash
        const { error: otpError } = await supabase.auth.verifyOtp({
          token_hash: result.token_hash,
          type: 'recovery',
        });

        if (otpError) throw otpError;

        setStatus('success');
        localStorage.removeItem('forgot_pw_cooldown_at');

        // Set session cookie (short-lived — recovery session only)
        // Secure flag on HTTPS; Max-Age=1h so it expires even if user closes tab
        const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
        const secureFlag = isHttps ? '; Secure' : '';
        document.cookie = `session_active=1; path=/; SameSite=Lax; Max-Age=3600${secureFlag}`;

        // Redirect to reset password page
        setTimeout(() => {
          onClose();
          window.location.href = '/reset-password';
        }, 1000);
      } else if (result?.status === 'expired') {
        setStatus('error');
        setError('Code has expired. Please request a new one.');
      } else if (result?.status === 'invalid') {
        setStatus('error');
        setError('Invalid code. Please check and try again.');
      } else {
        setStatus('error');
        setError('Code not found. Please request a new one.');
      }
    } catch (err) {
      if (import.meta.env?.DEV) console.error('verify-reset-code:', err);
      setStatus('error');
      setError('Verification failed. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-md rounded-2xl p-6 sm:p-8 overflow-hidden"
        style={{
          background: 'rgba(26, 26, 29, 0.95)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
        }}
      >
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-300 transition-colors">
          <Icon name="X" size={20} />
        </button>

        {/* Step 1: Enter Email */}
        {step === 1 && (
          <>
            <div className="text-center mb-6">
              <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2">Forgot Password?</h2>
              <p className="text-sm text-gray-400">
                Enter your email and we'll send you a code to reset your password.
              </p>
            </div>

            <form onSubmit={handleSendCode}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                <input
                  ref={inputRef}
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError('');
                    if (status === 'error') setStatus('idle');
                  }}
                  placeholder="Enter your email"
                  disabled={status === 'sending'}
                  className="w-full px-4 py-3.5 bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:border-transparent focus:shadow-[0_0_0_1px_rgba(254,131,56,0.3),0_0_20px_rgba(254,131,56,0.15),0_0_40px_rgba(249,110,34,0.1),0_0_60px_rgba(236,103,30,0.05)] transition-all duration-300 shadow-[inset_0_2px_8px_rgba(255,255,255,0.1)] disabled:opacity-50"
                />
                {error && <p className="text-red-400 text-xs mt-1.5">{error}</p>}
              </div>

              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                loading={status === 'sending'}
                disabled={status === 'sending' || cooldown > 0}
                className="bg-[#2a2a2a] hover:bg-[#333333] text-white border border-[#2a2a2a] rounded-2xl py-3.5 font-medium transition-all shadow-[inset_0_2px_8px_rgba(255,255,255,0.1)] focus:outline-none focus:shadow-[0_0_0_1px_rgba(254,131,56,0.3),0_0_20px_rgba(254,131,56,0.15),0_0_40px_rgba(249,110,34,0.1),0_0_60px_rgba(236,103,30,0.05)]"
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Send Reset Code'}
              </Button>
            </form>
          </>
        )}

        {/* Step 2: Enter Verification Code */}
        {step === 2 && (
          <>
            <div className="text-center mb-6">
              <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2">Enter Reset Code</h2>
              <p className="text-sm text-gray-400">
                We've sent an 8-character code to <span className="text-white font-medium">{email}</span>
              </p>
            </div>

            {/* Success Message */}
            {status === 'success' && (
              <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-4 mb-4">
                <div className="flex items-center gap-3">
                  <Icon name="CheckCircle2" size={20} className="text-green-400" />
                  <p className="text-green-400 text-sm font-medium">Code verified! Redirecting...</p>
                </div>
              </div>
            )}

            <div className="mb-4">
              <VerificationCodeInput
                code={code}
                onChange={setCode}
                error={error}
                disabled={status === 'verifying' || status === 'success'}
              />
            </div>

            <div className="space-y-3">
              <button
                onClick={handleVerifyCode}
                disabled={code?.length !== 8 || status === 'verifying' || status === 'success'}
                className="w-full px-4 py-3 sm:py-3.5 rounded-2xl bg-[#2a2a2a] hover:bg-[#333333] text-white text-sm sm:text-base font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-[inset_0_2px_4px_rgba(255,255,255,0.1)] flex items-center justify-center gap-2"
              >
                {status === 'verifying' ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Verifying...
                  </>
                ) : 'Verify Code'}
              </button>

              {/* Back to email */}
              <button
                onClick={() => { setStep(1); setCode(''); setError(''); setStatus('idle'); }}
                disabled={status === 'verifying' || status === 'success'}
                className="w-full text-sm text-gray-500 hover:text-gray-300 transition-colors flex items-center justify-center gap-1 py-2"
              >
                <Icon name="ArrowLeft" size={14} />
                Change email
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordModal;
